import React, { useState, useEffect } from "react";
import { Box, SelectChangeEvent } from "@mui/material";
import { Grid } from "@material-ui/core";
import CustomModal from "../../components/common/customModal";
import CustomInput from "../../components/common/inputField";
import LogoUpload from "../../components/common/logoUpload";
import CustomSelect from "../../components/common/customSelect";
import {
  PerformanceManagementForm,
  PerformanceManagementFormErrors,
  PerformanceManagementList,
} from "../../types/dashboard";
import { showToast } from "../../components/common/toastNotification";
import dashboardService from "../../services/DashboardServices";
import { getLastThreeFinancialYears } from "../../helper/getLastThreeFinancialYears";

interface PerformanceManagementProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  updatepPerformanceData: PerformanceManagementList;
}

const PerformanceManagement: React.FC<PerformanceManagementProps> = ({
  open,
  onClose,
  onSave,
  updatepPerformanceData,
}) => {
  const [formData, setFormData] = useState<PerformanceManagementForm>({
    id: null,
    title: "",
    year: "",
    logoUrl: "",
    logo: null,
    driveLink: "",
  });
  const [formErrors, setFormErrors] = useState({
    title: "",
    year: "",
    driveLink: "",
    logoUrl: "",
  });

  useEffect(() => {
    if (open) {
      if (Object.keys(updatepPerformanceData).length > 0) {
        setFormData({
          id: updatepPerformanceData.id || null,
          title: updatepPerformanceData.title || "",
          year: updatepPerformanceData.year || "",
          logoUrl: updatepPerformanceData.logoUrl || "",
          driveLink: updatepPerformanceData.driveLink || "",
        });
      } else {
        setFormData({
          id: null,
          title: "",
          year: "",
          logoUrl: "",
          logo: null,
          driveLink: "",
        });
      }
      setFormErrors({
        title: "",
        year: "",
        driveLink: "",
        logoUrl: "",
      });
    }
  }, [updatepPerformanceData, open]);

  //// Input onchange code
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  /// hande Drop down onchange code
  const handleYearOnChange = (event: SelectChangeEvent<string | number>) => {
    const { value } = event.target;

    setFormData((prev) => ({
      ...prev,
      ["year"]: value,
    }));

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      ["year"]: "",
    }));
  };

  /// handle file upload logo
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, logo: file }));
    if (file) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        logoUrl: "",
      }));
    }
  };

  /// handle Form validation code
  const validateForm = () => {
    const errors: PerformanceManagementFormErrors = {};

    const urlRegex =
      /^(https?:\/\/)(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})(\/[^\s]*)?$/i;

    if (!formData.title.trim()) {
      errors.title = "Title is required.";
    }

    if (!formData.year) {
      errors.year = "Year is required.";
    }

    if (!formData.driveLink.trim()) {
      errors.driveLink = "Google Drive link is required.";
    } else if (!urlRegex.test(formData.driveLink.trim())) {
      errors.driveLink = "Enter a valid Google Drive link.";
    }

    const logoUrl = formData.logoUrl?.trim() || "";
    if (!logoUrl && !formData.logo) {
      errors.logoUrl = "Either a file or a URL must be provided.";
      showToast(errors.logoUrl, "error");
    } else if (logoUrl && !urlRegex.test(logoUrl)) {
      errors.logoUrl = "Enter a valid URL for the logo.";
    }

    setFormErrors({
      title: errors.title || "",
      year: errors.year || "",
      driveLink: errors.driveLink || "",
      logoUrl: errors.logoUrl || "",
    });

    return Object.keys(errors).length === 0;
  };

  //// handle Save Data
  const handleSaveData = async () => {
    if (!validateForm()) return;
    try {
      const { id, title, year, driveLink, logoUrl, logo } = formData;

      const formDataToSend = new FormData();
      formDataToSend.append("Id", id?.toString() || "0");
      formDataToSend.append("Year", String(year));
      formDataToSend.append("Title", title.trim());
      formDataToSend.append("DriveLink", driveLink.trim());

      if (logo instanceof File) {
        formDataToSend.append("Logo", logo);
        formDataToSend.append("LogoUrl", "");
      } else if (logoUrl?.trim()) {
        formDataToSend.append("LogoUrl", logoUrl.trim());
        formDataToSend.append("Logo", "");
      }

      const apiCall = id
        ? dashboardService.updatePerformanceManagementDataAPI
        : dashboardService.addPerformanceManagementDataAPI;

      await apiCall(formDataToSend)
        .then((response) => {
          if (response && response.data) {
            showToast(response.data.message, "success");
            setFormData({
              id: null,
              title: "",
              year: "",
              logoUrl: "",
              logo: null,
              driveLink: "",
            });
            onSave();
            onClose();
          } else {
            showToast(response?.data?.message || "Error occurred", "error");
          }
        })
        .catch((error) => {
          console.error("Error", error);
        });
    } catch (error) {
      console.error("Error:", error);
    }
  };

  return (
    <CustomModal
      open={open}
      handleClose={onClose}
      onSave={handleSaveData}
      title="Performance Management"
    >
      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CustomInput
              label="Title"
              type="text"
              name="title"
              placeholder="Enter Title"
              value={formData.title}
              onChange={handleChange}
              error={formErrors.title}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <CustomSelect
              name="Year"
              label="Year"
              placeHolder="Select Year"
              value={formData.year || ""}
              onChange={handleYearOnChange}
              error={formErrors.year}
              required
              options={getLastThreeFinancialYears()}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <LogoUpload
              label="Upload Logo"
              onChange={handleFileUpload}
              fileName={formData.logo instanceof File ? formData.logo.name : ""}
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <CustomInput
              label="Upload logo via the URL"
              type="text"
              name="logoUrl"
              placeholder="Enter URL"
              value={formData.logoUrl || ""}
              onChange={handleChange}
              error={formErrors.logoUrl}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              label="URL of the google drive Link"
              type="text"
              name="driveLink"
              placeholder="Enter the URL of the google drive Link"
              value={formData.driveLink}
              onChange={handleChange}
              error={formErrors.driveLink}
              required
            />
          </Grid>
        </Grid>
      </Box>
    </CustomModal>
  );
};

export default PerformanceManagement;
